import React, { useState } from 'react';
import { Container, Card, CardHeader, CardBody, Row, Col, FormGroup, Label, Button, Input } from 'reactstrap';
import classNames from 'classnames';
import styles from './CoverageVerification.module.css';
import { ReactComponent as ShieldBackground } from '../../assets/images/BgSearch.svg';
import { ReactComponent as Shield } from '../../assets/images/Shield_Check_success_new.svg';
import SpinnerButton from '../shared/SpinnerButton/SpinnerButton';
import { ContractStatus } from '../Contract/ContractStatus';

const CoverageVerification = ({ data }) => {
    const [serialNumber, setSerialNumber] = useState('');
    const [showSerialNumber, setShowSerialNumber] = useState('');
    const [plans, setPlans] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [error, setError] = useState(false);
    const [noPlanError, setNoPlanError] = useState(false);

    const loadPlans = async (serialNumber) => {
        setShowSpinner(true);
        try {
            await fetch('contract/GetContractCoverageInformationBySerial', {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    serialNumber: serialNumber
                })
            }).then((res) => {
                if (res.status === 200) {
                    res.json().then((data) => {
                        setPlans(data);
                        if (data.length === 0) {
                            setNoPlanError(true);
                        }
                    });
                }
            });
        } catch (error) {
            console.error('Error ===>>> ', error);
            setNoPlanError(true);
        }
        setShowSpinner(false);
        setSerialNumber('');
    };

    const handleInputChange = (e, value) => {
        e.preventDefault();
        setNoPlanError(false);
        if (value.trim() !== '') {
            setError(false);
        } else {
            setError(true);
        }

        setSerialNumber(value);
    };

    const handleSearch = () => {
        setNoPlanError(false);
        if (serialNumber.trim() === '') {
            setError(true);
            return;
        }

        setShowSerialNumber(serialNumber);
        loadPlans(serialNumber);
    };

    return (
        <Container className='contentWrapper pb-3'>
            <Card className={styles.searchContainer}>
                <CardHeader className={classNames(styles.headerTop, 'my-0')}>HVAC Coverage Lookup</CardHeader>
                <CardBody className={classNames(styles.shieldSize, 'pt-0')}>
                    <ShieldBackground className={styles.shieldBackground} />
                    <div>
                        <Row className={classNames('multiColWrapper', styles.searchText)}>
                            <Col className='col-md-6'>
                                <p>
                                    This tool can help you verify warranty coverage on a piece of equipment. Search
                                    using a valid serial number.
                                </p>
                                <p>For additional support, contact Dealer Support at (866) 513-1376.</p>
                            </Col>
                        </Row>
                        <Row className='multiColWrapper'>
                            <Col className='col-md-3'>
                                <FormGroup className={error && `form-error`}>
                                    <label
                                        for='serialNumber'
                                        className='form-label'
                                    >
                                        Serial Number
                                    </label>
                                    <span
                                        aria-hidden='true'
                                        className='required-indicator'
                                    >
                                        *
                                    </span>
                                    <Input
                                        name='serialNumber'
                                        className='form-control'
                                        value={serialNumber}
                                        onChange={(e) => handleInputChange(e, e.target.value)}
                                        onPaste={(e) => handleInputChange(e, e.clipboardData.getData('text'))}
                                    />
                                    {error && <div className='errorMsg'>Serial number is required.</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className={styles.searchButton}>
                            <Col>
                                <SpinnerButton
                                    onClick={handleSearch}
                                    spinning={showSpinner}
                                    styleType='primary'
                                    text='Search'
                                />

                                {noPlanError && (
                                    <div className='errorMsg mt-4'>
                                        <strong>A plan could not be located with the information provided.</strong>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </CardBody>
            </Card>

            <Container>
                {plans?.length > 0 && (
                    <>
                        <Row className='pb-0'>
                            <Col className='pl-0'>
                                <h4 className={styles.resultContainerHeading}>
                                    Search results for: #{showSerialNumber}
                                </h4>
                                <p>
                                    Results do not guarantee the existence of coverage, all contracts are subject to the
                                    terms and conditions of the program.
                                </p>
                            </Col>
                        </Row>

                        <Row>
                            {plans?.map((plan, index) => (
                                <Col
                                    className={classNames(
                                        'col-md-6 px-0 mx-0',
                                        index > 1 && styles.marginTop,
                                        (index + 1) % 2 === 0 && 'd-flex justify-content-end mr-0'
                                    )}
                                >
                                    <Card
                                        className={classNames(
                                            styles.resultContainer,
                                            (index + 1) % 2 === 0 && styles.marginLeft,
                                            (index + 1) % 2 !== 0 && styles.marginRight,
                                            'mb-0'
                                        )}
                                    >
                                        <CardHeader>
                                            <h3 className={styles.resultHeading}>
                                                {plan.termName
                                                    + ' '
                                                    + plan.coverageTypeDescription
                                                    + ' '
                                                    + plan.tierName
                                                    + ' '
                                                    + plan.assetPackageDescription}
                                            </h3>
                                            <ContractStatus
                                                statusId={plan.statusId}
                                                customClass='ml-0'
                                            />
                                        </CardHeader>
                                        <CardBody>
                                            <p>
                                                Plan Begin Date: <strong>{plan.enforcementDate}</strong>
                                            </p>
                                            <p>
                                                Plan Expiration Date: <strong>{plan.expirationDate}</strong>
                                            </p>
                                            <p>
                                                Plan Details: <strong>{plan.riskProfileDescription}</strong>
                                            </p>
                                            <p>
                                                Contract: <strong>#{plan.contractNumber}</strong>
                                            </p>
                                            <div className={styles.shieldContainer}>
                                                <Shield />
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
                <Row className={styles.footerText}>
                    <Col className='px-0'>
                        <p>
                            Limitations and exclusions apply. In the United States, extended service contracts are
                            provided by Service Net Warranty, LLC, part of AIG, or one or more of its affiliates. In
                            Canada, extended service contracts are provided by AIG Insurance Company of Canada, SNAdmin
                            (Canada), Inc., or other member companies of AIG. In Canada, non-insurance service benefits
                            may be provided by other third parties. See terms and conditions for further information.
                            AIG is the marketing name for the worldwide general insurance operations of American
                            International Group, Inc. For additional information, please visit our website at{' '}
                            <a
                                href='https://www.aig.com'
                                target='_blank'
                            >
                                www.aig.com
                            </a>
                            .
                        </p>
                        <p>
                            American International Group, Inc, (AIG), is a global insurance organization providing a
                            range of insurance and other financial services. Get to know us better.
                        </p>
                    </Col>
                </Row>
            </Container>
        </Container>
    );
};
export default CoverageVerification;
